import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { DeviceStatus } from '../../constants/DeviceStatus';
import {
  toNumber,
  floatRemainder,
  getAverage,
} from '../../utils/numbers';
import {
  JogType,
  BatchMode,
  MeteredMaterialType,
} from '../../constants/MaterialModels';
import { DynamicJog } from './jog/dynamic-jog';
import moment from 'moment';
import { reaction } from 'mobx';
import type {
  IMeteredDataToBatch,
  IWeighedDataToBatch,
} from '../../../types/batch/DataToBatch';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import convertUnit from '../../utils/unit-converter';
import { aeratorSwitch } from './device-switches/aerator-switch';
import type { IVibrator } from '../../../types/device/DeviceModels';
import type {
  IBinEntry,
  IMaterial,
  IMaterialWithDetails,
} from '../../../types/material/MaterialModels';
import { RemoteDisplayBatchStatus } from '../../constants/RemoteDisplayBatchStatus';
import {
  getAutoMoistureWithinChangeLimit,
  waterShouldWaitForMoistureProbe,
} from './live-moisture';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { checkIfDischargeStartPercentForPumpsReady } from './checkAdmixtureDischargeConditions';
import { VibratorMode } from '../../constants/DeviceModels';
import { vibratorSwitch } from './device-switches/vibrator-switch';
import { screwSwitch } from './device-switches/screw-switch';
import { addFeedLog } from '../../containers/Batch/DataToBatch';
import { FeedOperation } from '../../constants/FeedOperation';
import {
  asyncForEach,
  waitFor,
} from '../../utils/async';

const FEATURE_LIVE_MOISTURE_FOR_WEIGHED = isFeatureEnabled('live_moisture_for_weighed');
const FEATURE_AUTO_TUNING = isFeatureEnabled('auto_tuning');

export const jogHelpersMixin = {

  checkScaleWeightInTestMode(this: IBatchStore, data: IWeighedDataToBatch, runningData: IBatchData) {

    const { material, scale, mft } = data;
    let md = this.materialStore.materialDetails[material.materialDetailId];
    const bins = this.getBins(data);
    const primaryBin = bins.primaryBin;
    const freeFall = this.getFreeFall(primaryBin, runningData);
    let status = DeviceStatus.FEED;
    const target = this.getNetTargetForScale(scale.id, runningData) + mft;

    const onFinish = (
      reaction: any,
      runningData: IBatchData,
      data: IWeighedDataToBatch,
      weight: number,
    ): void => {
      if (this.isOverride) {
        reaction.dispose();
        this.updateInventory(data, weight, runningData);
        this.finishFeed(data, runningData, weight);
      }
    };

    logger.log('start check material test mode', material.code, scale.description, mft, target);

    reaction(
      () => this.statusCount,
      (counter, reaction) => {

        if (this.isAbort || !this.isActiveDrop(runningData) || !this.running.length) {
          logger.log('end check material', material.code);
          reaction.dispose();
        }

        if (runningData.isHoldLoad) {
          return;
        }

        md = this.materialStore.materialDetails[material.materialDetailId];
        const tolerance = this.getTolerance(md, target);
        const bs = this.getDeviceStatus(primaryBin.id);
        const weight = this.getScaleWeight(scale);
        let error = false;

        if (weight > target + tolerance?.over) {
          status = bs.overridden ? DeviceStatus.FINISH : DeviceStatus.OVER;
          error = !bs.overridden;
          if (bs.overridden) {
            onFinish(reaction, runningData, data, weight);
          }
        } else if (weight >= target - tolerance?.under) {
          status = DeviceStatus.FINISH;
          if (this.isOverride) {
            onFinish(reaction, runningData, data, weight);
          }
        } else if (weight >= target - freeFall) {
          status = DeviceStatus.JOG;
        } else if (weight < target - freeFall) {
          status = DeviceStatus.FEED;
        }

        this.deviceStatus.set(scale.id, {
          ...this.getDeviceStatus(scale.id),
          status,
          weight,
        });

        this.deviceStatus.set(primaryBin.id, {
          ...this.getDeviceStatus(primaryBin.id),
          error,
          status,
          weight,
        });

        logger.log('check weight test mode', material.code, scale.description,
          status, mft, target, weight, freeFall, tolerance);
      },
    );
  },

  checkPumpInTestMode(this: IBatchStore, data: IMeteredDataToBatch, runningData: IBatchData) {

    const { material, materialDetails, pump, mft } = data;
    const startWeight = this.getPumpWeight(pump);

    const onFinish = (
      reaction: any,
      runningData: IBatchData,
      weight: number,
    ) => {
      logger.log('on finish test mode', pump.description, material.code, weight);
      runningData.pumps[pump.id].isDischarged = true;

      if (materialDetails.type === MeteredMaterialType.Water) {
        runningData.pumps[pump.id].headWaterDischarged = true;
        runningData.pumps[pump.id].washWaterDischarged = true;
        runningData.headWaterPumpDischarged = true;
        runningData.washWaterPumpDischarged = true;
      }

      runningData.mixDesign?.forEach((v) => {
        if ((v.batchCode && v.batchCode === material.batchCode) || v.constituentItemId === material.itemId) {
          let actualWeight = weight;
          let targetPerDrop = mft;

          if (pump.unitId && pump.unitId !== materialDetails.itemBatchUnitId) {
            const pumpUnit = this.getUnitById(pump.unitId);
            actualWeight = toNumber(convertUnit(weight, pumpUnit, materialDetails.itemBatchUnitSymbol), 2);
            targetPerDrop = toNumber(convertUnit(mft, pumpUnit, materialDetails.itemBatchUnitSymbol), 2);
          }

          v.actualWeight = actualWeight;
          v.actualPerDrop = actualWeight;
          v.targetPerDrop = targetPerDrop;
          logger.log('actual weight', material.code, weight, actualWeight, targetPerDrop);
          this.saveResultForDrop(runningData, v);
        }
      });

      this.checkScalesIfWaitingForWater(runningData);
      reaction.dispose();
    };

    logger.log('start check material test mode', material.code, pump.description);

    reaction(
      () => this.statusCount,
      (counter, reaction) => {

        if (this.isAbort || !this.isActiveDrop(runningData) || !this.running.length) {
          logger.log('end check material', material.code);
          reaction.dispose();
        }

        if (runningData.isHoldLoad) {
          return;
        }

        const md = this.materialStore.materialDetails[material.materialDetailId];
        const unit = md.itemBatchUnitSymbol || '';
        const meterRate = md.meterRate || 1;
        const d = this.getDeviceStatus(pump.id);
        const w = this.getPumpWeight(pump);
        const weight = (w - startWeight) * meterRate;
        const tolerance = this.getTolerance(md, mft);
        let status = DeviceStatus.FEED;
        let error = false;

        if (
          d.status === DeviceStatus.WAIT_DISCH
          && this.isDischarge
          && this.discharging
          && runningData.batchActionFired.includes(md.dischargeStart)
          && this.isBottlePump(pump)
          && this.isPumpEmpty(pump)
        ) {
          status = DeviceStatus.FINISH;
        } else if (weight && weight > mft + tolerance?.over) {
          status = d.overridden || d.errorsOverridden.includes(DeviceStatus.OVER)
            ? this.isBottlePump(pump)
              ? DeviceStatus.WAIT_DISCH
              : DeviceStatus.FINISH
            : DeviceStatus.OVER;
          error = status === DeviceStatus.OVER;
        } else if (weight >= mft) {
          status = this.isBottlePump(pump) ? DeviceStatus.WAIT_DISCH : DeviceStatus.FINISH;
        }

        if (status === DeviceStatus.FINISH) {
          onFinish(reaction, runningData, weight);
        }

        logger.log('check pump test mode', material.code, status, mft, tolerance, weight);
        this.deviceStatus.set(pump.id, {
          ...d,
          unit,
          status,
          weight,
          error,
        });
      },
    );
  },

  checkScaleWeightForMaterial(this: IBatchStore, data: IWeighedDataToBatch) {

    const { material, scale, mft } = data;
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const bins = this.getBins(data);
    const primaryBin = bins.primaryBin;
    logger.log('start check material', material.code);

    reaction(
      () => this.statusCount,
      (counter, reaction) => {

        const scaleWeight = this.getScaleWeight(scale);
        const binStatus = this.getDeviceStatus(primaryBin.id);
        const target = mft + binStatus.prevWeight;

        if (primaryBin?.aeratorId && scaleWeight >= target - (materialDetails.aerationOff || 0)) {
          aeratorSwitch(this.rootStore, primaryBin.aeratorId, false);
        }

        if (this.isAbort || !this.running.length || scaleWeight >= target || binStatus.status === DeviceStatus.FINISH) {
          logger.log('end check material', material.code);
          reaction.dispose();
        }

      },
    );
  },

  checkFeedWeight(
    this: IBatchStore,
    data: IWeighedDataToBatch,
    initialData: IBatchData,
    mft: number,
    onFinish: (weight?: number) => void = () => {/* empty */},
  ): void {

    const { remoteDisplayStore } = this.rootStore;
    const { material, scale, isTargetAdjusted, isFeedFinish } = data;
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const materialName = this.materialStore.getMaterialName(material);
    const bins = this.getBins(data);
    const primaryBin = bins.primaryBin;
    const secondaryBin = bins.secondaryBin;
    const startWeight = this.getCurrentWeightForBin(primaryBin.id);
    const { configuration } = this.settingsStore;

    const tolerance = this.getTolerance(materialDetails, mft);
    let flowRateSamples: number[] = [];
    let vibrator: IVibrator;

    if (primaryBin?.vibratorId) {
      vibrator = this.devicesStore.getDeviceById(primaryBin.vibratorId);
    }

    if (this.isAbort || !this.isActiveDrop(initialData) || isFeedFinish) {
      return;
    }

    let isReadyForMoistureProbe: boolean = false;
    let moistureProbe: number[] = [];
    let analogInputId: number|null = null;
    let moistureProbeTimer: any;

    if ((materialDetails.autoMoisture || materialDetails.liveMoisture) && !isTargetAdjusted) {
      // Determine which bin's moisture probe to use based on active status
      const primaryBinAssignment = materialDetails.bins?.find((b: IBinEntry) => b.binId === primaryBin?.id);
      const isPrimaryActive = primaryBinAssignment?.isActive !== false;

      if (isPrimaryActive && primaryBin?.analogInputId) {
        analogInputId = primaryBin.analogInputId;
      } else if (secondaryBin?.analogInputId) {
        analogInputId = secondaryBin.analogInputId;
      } else if (primaryBin?.analogInputId) {
        // If the secondary bin is missing analogInputId,
        // the system should fall back to primary bin anyway.
        analogInputId = primaryBin.analogInputId;
        logger.warn(`Secondary bin for ${material.code} is missing analog input id`);
      }

      if (analogInputId) {
        moistureProbeTimer = this.setTimeout(() => {
          isReadyForMoistureProbe = true;
          logger.log('moisture probe ready', material.code);
        }, (materialDetails.autoMoistureDelay || 2) * 1000);
      } else {
        logger.warn(`Can't ready moisture probe. Bins for ${material.code} missing analog input id`);
      }
    }

    const getNewTargetForFeed = () => {
      return mft + this.getFreeFall(primaryBin, initialData);
    };

    reaction(
      () => this.statusCount,
      (counter, reaction) => {

        if (this.isAbort || !this.isActiveDrop(initialData)) {
          reaction.dispose();
          this.clearTimeout(moistureProbeTimer);
          this.closeBin(primaryBin);

          if (secondaryBin) {
            this.closeBin(secondaryBin);
          }

          return;
        }

        const weight = this.getCurrentWeightForBin(primaryBin.id);
        const primaryBinStatus = this.getDeviceStatus(primaryBin.id);

        if (scale.remoteDisplayId && weight > (startWeight + scale.startTare)) {
          remoteDisplayStore.setStatus(scale.remoteDisplayId, {
            status: RemoteDisplayBatchStatus.WEIGHT,
            balance: mft-weight,
          });

          if (weight >= mft - tolerance?.under) {
            const b1s = this.getDeviceStatus(primaryBin.id);
            this.deviceStatus.set(primaryBin.id, {
              ...b1s,
              weight: this.getBinWeightForStatus(b1s, weight),
              status: DeviceStatus.SETTLING,
              error: false,
            });

            remoteDisplayStore.setStatus(scale.remoteDisplayId, {
              status: RemoteDisplayBatchStatus.DISCHARGE,
              balance: '0',
            });

            onFinish(weight);
            reaction.dispose();
          }
          return;
        }

        if (materialDetails.batchMode === BatchMode.Manual) {
          if (weight >= mft - tolerance?.under && !this.isOutputOn(primaryBin.gateOnePNo)) {
            onFinish(weight);
            reaction.dispose();
          }
          return;
        }

        //pause the water scale feeding at 70% until the aggregates are complete
        if (
          FEATURE_LIVE_MOISTURE_FOR_WEIGHED
          && weight >= (mft * 0.7)
          && (
            this.materialStore.isWater(material.itemId)
            || (
              this.materialStore.isSecondWater(material.itemId)
              && toNumber(initialData.secondWater) === 100
            )
          )
          && waterShouldWaitForMoistureProbe(initialData, this)
        ) {
          // if no jogs in the current drop we don't want to stop the water
          if (
            configuration.onlyJogOnLastDrop
            && initialData.allDrops > 1
            && initialData.currentDrop !== initialData.allDrops
            && initialData.mixDesign
          ) {
            logger.log(material.code, 'no jogs in the current drop, should not stop water');
          } else {
            this.resetWeight(scale.inputPNo);
            this.closeBin(primaryBin);

            this.deviceStatus.set(primaryBin.id, {
              ...primaryBinStatus,
              status: DeviceStatus.WAIT_MOIST,
            });

            if (secondaryBin) {
              this.closeBin(secondaryBin);

              this.deviceStatus.set(secondaryBin.id, {
                ...this.getDeviceStatus(secondaryBin.id),
                status: DeviceStatus.WAIT_MOIST,
              });
            }

            logger.log('live moisture, pause the water scale feeding at 70% until the aggregates are complete', material.code, mft, weight);

            this.clearTimeout(moistureProbeTimer);
            reaction.dispose();

            return;
          }
        }

        if (this.isHoldFeed || initialData.isHoldLoad || this.isHoldFeedDevice(primaryBin.id)) {
          this.resetWeight(scale.inputPNo);

          this.holdBin(primaryBin);

          if (secondaryBin) {
            this.holdBin(secondaryBin);
          }

          logger.log('check feed weight', material.code, mft, weight);
          this.feedMode(data, initialData, getNewTargetForFeed());

          this.clearTimeout(moistureProbeTimer);
          reaction.dispose();

          return;
        }

        if (this.isOutputOn(primaryBin.gateOnePNo)) {
          this.deviceStatus.set(primaryBin.id, {
            ...primaryBinStatus,
            weight: this.getBinWeightForStatus(primaryBinStatus, weight),
          });
        }

        if (secondaryBin && this.isOutputOn(secondaryBin.gateOnePNo)) {
          const b2s = this.getDeviceStatus(secondaryBin.id);
          this.deviceStatus.set(secondaryBin.id, {
            ...b2s,
            weight: this.getBinWeightForStatus(b2s, weight),
          });
        }

        if (this.isFinishFeedDevice(primaryBin.id)) {
          this.resetWeight(scale.inputPNo);
          this.clearTimeout(moistureProbeTimer);
          this.closeBin(primaryBin);

          if (secondaryBin) {
            this.closeBin(secondaryBin);
            this.setWeight('+', scale.inputPNo, -15000, -15000, secondaryBin.gateOnePNo, primaryBin.gateOnePNo);
          } else {
            this.setWeight('+', scale.inputPNo, -15000, -15000, 0, primaryBin.gateOnePNo);
          }

          reaction.dispose();
          onFinish();
          return;
        }

        if (isReadyForMoistureProbe && analogInputId) {
          const moisture = this.getAnalogInputValue(analogInputId);
          if (mft >= materialDetails.autoMoistureMinWeight) {
            moistureProbe.push(moisture);
          }
        }

        if (this.weightsStatus[scale.inputPNo - 1] === '2') {
          logger.log('feed finish', material.code, weight, mft, tolerance, initialData.uid, 'moisture', moistureProbe);

          if (moistureProbe.length >= 5) {
            const autoMoistureAmount = toNumber(getAverage(moistureProbe), 1);
            logger.log('auto moisture average:', material.code, autoMoistureAmount, moistureProbe);

            if (autoMoistureAmount <= materialDetails.autoMoistureMaxAmount && autoMoistureAmount >= materialDetails.autoMoistureMinAmount) {
              if (autoMoistureAmount === materialDetails.autoMoistureAmount) {
                logger.log('auto moisture not set', material.code, materialDetails.autoMoistureAmount, autoMoistureAmount,
                  'current drop:', initialData.currentDrop);
              } else {
                materialDetails.autoMoistureAmount = getAutoMoistureWithinChangeLimit(autoMoistureAmount, materialDetails);

                if (!this.isDemo) {
                  this.materialStore.saveMaterialDetail({ ...material }, { ...materialDetails });
                }
                logger.log('new auto moisture set', material.code, materialDetails.autoMoistureAmount, 'current drop:', initialData.currentDrop);
              }
            } else {
              const materialCode = this.materialStore.getMaterialName(material);
              this.showWarning({ key:  'Moisture is out of range', props: { materialCode, autoMoistureAmount }});
              logger.log(`${materialCode}: auto moisture`, autoMoistureAmount,  'is out of range! min:',
                materialDetails.autoMoistureMinAmount, 'max:', materialDetails.autoMoistureMaxAmount,
                `or is DEMO: ${this.isDemo}`);
            }

          }

          this.clearTimeout(moistureProbeTimer);

          this.deviceStatus.set(primaryBin.id, {
            ...this.getDeviceStatus(primaryBin.id),
            error: false,
          });

          reaction.dispose();
          onFinish(weight);
          return;
        }

        if (primaryBinStatus.status === DeviceStatus.FINISH) {
          logger.warn('check feed weight: bin already finished', scale.description, primaryBin.description, materialName);
          reaction.dispose();
          onFinish(weight);
          return;
        }

        if (this.isScaleOpen(scale) && primaryBinStatus.status === DeviceStatus.FEED) {
          const error = `Scale ${scale.description} opened during feed. ${materialName} is on hold`;
          logger.warn(error);
          this.showError(error);
          this.closeScale(scale);
          this.holdFeedDevice(primaryBin.id);
        }

        checkIfDischargeStartPercentForPumpsReady(this.rootStore, initialData);

        this.onFeedFlowRateChange(scale, (flowRate) => {
          if (this.getDeviceStatus(primaryBin.id).status !== DeviceStatus.FEED) {
            return;
          }

          flowRateSamples.push(flowRate);

          if (flowRateSamples.length === 25) {
            let flowRateAverage = toNumber(flowRateSamples.reduce((a, b) => a + b, 0) / (flowRateSamples.length/5), 2);
            logger.log(material.code, 'feed flow rate:', flowRateAverage, flowRateSamples);

            if (materialDetails.minimumFlowRate && flowRateAverage < materialDetails.minimumFlowRate) {
              logger.log(`${material.code}: feed flow rate drop below minimum ${materialDetails.minimumFlowRate} ${materialDetails.itemBatchUnitSymbol}/s`, flowRateAverage);
              this.showError({ key: 'Flow rate drop below minimum', props: { material: material.code, rate: materialDetails.minimumFlowRate, unit: materialDetails.itemBatchUnitSymbol }});

              this.deviceStatus.set(primaryBin.id, {
                ...this.getDeviceStatus(primaryBin.id),
                error: true,
              });
            } else {
              this.deviceStatus.set(primaryBin.id, {
                ...this.getDeviceStatus(primaryBin.id),
                error: false,
              });
            }

            flowRateSamples = [];
          }

          if (vibrator && vibrator.mode === VibratorMode.MinFlow) {
            if (flowRate < (materialDetails.minimumFlowRate || 0) && weight < mft - tolerance?.under) {
              vibratorSwitch(this.rootStore, primaryBin.vibratorId);
            } else {
              vibratorSwitch(this.rootStore, primaryBin.vibratorId, false);
            }
          }
        });

        if (primaryBin.screwId && materialDetails.screwOffAmount && weight >= mft - materialDetails.screwOffAmount) {
          logger.log('screw off', material.code, weight, mft, materialDetails.screwOffAmount, weight >= mft - materialDetails.screwOffAmount);
          screwSwitch(this.rootStore, primaryBin.screwId, false);
        }

      },
    );
  },

  getTolerance(this: IBatchStore, materialDetails: IMaterialWithDetails, target: number): { over: number, under: number } {
    return {
      over: this.getToleranceOver(materialDetails, target),
      under: this.getToleranceUnder(materialDetails, target),
    };
  },

  getToleranceOver(this: IBatchStore, materialDetails: IMaterialWithDetails, target: number): number {

    if (target <= 0 || !materialDetails) {
      return 0;
    }

    const percent = materialDetails.overToleranceByPercent
      ? toNumber(target * (materialDetails.overToleranceByPercent / 100), 2)
      : 0;
    const unit = target >= materialDetails.overToleranceByUnit ? materialDetails.overToleranceByUnit || 0 : 0;

    return Math.max(percent, unit);
  },

  getToleranceUnder(this: IBatchStore, materialDetails: IMaterialWithDetails, target: number): number {

    if (target <= 0 || !materialDetails) {
      return 0;
    }

    const percent = materialDetails.underToleranceByPercent
      ? toNumber(target * (materialDetails.underToleranceByPercent / 100), 2)
      : 0;
    const unit = target >= materialDetails.underToleranceByUnit ? materialDetails.underToleranceByUnit || 0 : 0;

    return Math.max(percent, unit);
  },

  getToleranceByPercent(this: IBatchStore, materialDetails: IMaterialWithDetails, target: number): { over: number, under: number } {
    return {
      over: (this.getToleranceOver(materialDetails, target) / target) * 100,
      under: (this.getToleranceUnder(materialDetails, target) / target) * 100,
    };
  },

  getJogOpenTime(this: IBatchStore, weight: number, jogTable: any, index = 1) {

    if (!jogTable || !weight) {
      return 0;
    }

    let time = 0;
    const arr = Object.keys(jogTable)
      .filter(v=> v.indexOf('weight') > -1)
      .reverse();

    arr.forEach((v, i)=> {
      const index = arr.length - i;
      const w = jogTable[`weight${index}`];

      if (!time) {
        if (w <= weight && w > 0) {
          time = jogTable[`open${index}`];
        }
      }

    });

    if (!time) {
      time = jogTable[`weight${index}`] > 0 ? jogTable[`open${index}`] : 0;
    }

    return !time && index < arr.length ? this.getJogOpenTime(weight, jogTable, index+1) : time;
  },

  openJog(this: IBatchStore, data: IWeighedDataToBatch, initialData: IBatchData, mft: number) {

    const { material, scale, jogSecondary } = data;
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const jogType = materialDetails.jogType || JogType.Primary;
    const bins = this.getBins(data);
    const primaryBin = bins.primaryBin;
    const secondaryBin = bins.secondaryBin;

    const bin = jogType === JogType.Alternate && secondaryBin && jogSecondary ? secondaryBin : primaryBin;

    if (this.isAbort || !this.isActiveDrop(initialData)) {
      return;
    }

    if (!bin) {
      logger.warn('No bin found for material: ', material.code);
      return;
    }

    if (this.isHoldFeed || initialData.isHoldLoad || this.isHoldFeedDevice(bin.id)) {
      this.jogMode(data, initialData, mft);
      return;
    }

    const currentWeight = this.getCurrentWeightForBin(bin.id);
    let jogOpenTime = this.getInitialJogOpenTime(bin, initialData);
    const autoJogRepeat = 2;

    if (bin.autoJog || bin.autoTuning) {
      const convertedWeight = toNumber(convertUnit(mft - currentWeight, this.getUnitById(scale.unitId), materialDetails.itemBatchUnitSymbol), 2);
      if (FEATURE_AUTO_TUNING && bin.autoTuning && bin.jogTableAuto) {
        jogOpenTime = this.getJogOpenTime(convertedWeight, bin.jogTableAuto) || jogOpenTime;
      } else if (bin.autoJog && bin.jogTable) {
        jogOpenTime = this.getJogOpenTime(convertedWeight, bin.jogTable) || jogOpenTime;
      } else {
        if (!data.dynamicJog) {
          data.dynamicJog = new DynamicJog(mft, jogOpenTime, currentWeight);
        } else if (data.dynamicJog?.getJogOpenTime) {
          jogOpenTime = data.dynamicJog?.getJogOpenTime(currentWeight);
          data.dynamicJogOpenTimes.push(jogOpenTime);
        }
      }

      if (currentWeight > 0 && currentWeight === data.lastWeight) {

        data = {
          ...data,
          autoJogOpenTime: data.autoJogOpenTime || jogOpenTime,
          autoJogCounter: data.autoJogCounter + 1,
        };

        jogOpenTime = data.autoJogOpenTime;

        if (data.autoJogCounter > autoJogRepeat && jogOpenTime < 3) {
          jogOpenTime = toNumber(data.autoJogOpenTime + 0.05, 2);

          data = {
            ...data,
            autoJogCounter: 1,
          };

        }
      }

      data = {
        ...data,
        autoJogOpenTime: jogOpenTime,
        lastWeight: currentWeight,
        autoJogCounter: data.autoJogCounter || 1,
      };

      if (jogOpenTime >= 3) {
        this.showWarning('Auto Jog Open Time reached maximum 3s!');
      }
    }

    logger.log('JOG open:', material.code, bin.description, jogOpenTime);

    const s = this.getDeviceStatus(scale.id);
    const d = this.getDeviceStatus(bin.id);

    this.deviceStatus.set(bin.id, {
      ...d,
      weight: this.getBinWeightForStatus(d, currentWeight),
      status: DeviceStatus.JOG,
    });

    this.deviceStatus.set(scale.id, {
      ...s,
      weight: currentWeight,
      status: DeviceStatus.JOG,
    });

    const port = bin.gateOnePNo;
    this.setOutput(port, `${jogOpenTime * 1000}`);

    addFeedLog({
      batchTicketId: initialData.id,
      operationName: FeedOperation.JOG_OPEN,
      duration: jogOpenTime,
      scaleWeight: this.getScaleWeight(scale),
      ...this.rootStore.weatherStore,
    }, data);

    this.timers.set(timerKey.jog(scale.id), () => {
      this.closeJog(data, initialData, mft, jogOpenTime, currentWeight);
    }, jogOpenTime * 1000);
  },

  closeJog(
    this: IBatchStore,
    data: IWeighedDataToBatch,
    initialData: IBatchData,
    mft: number,
    jogOpenTime: number,
    startJogWeight: number,
  ): void {

    const { material, materialDetails, scale, jogSecondary } = data;
    const jogType = materialDetails.jogType || JogType.Primary;
    const bins = this.getBins(data);
    const primaryBin = bins.primaryBin;
    const secondaryBin = bins.secondaryBin;

    const bin = jogType === JogType.Alternate && secondaryBin && jogSecondary ? secondaryBin : primaryBin;

    logger.log('JOG close:', material.code, bin.description, bin.jogCloseTime);

    if (this.isAbort || !this.isActiveDrop(initialData)) {
      return;
    }

    const d = this.getDeviceStatus(bin.id);
    const s = this.getDeviceStatus(scale.id);
    const currentWeight = this.getScaleWeight(scale);

    this.deviceStatus.set(bin.id, {
      ...d,
      weight: this.getBinWeightForStatus(d, currentWeight),
      prevStatus: DeviceStatus.JOG,
      status: DeviceStatus.SETTLING,
    });

    this.deviceStatus.set(scale.id, {
      ...s,
      weight: currentWeight,
      status: DeviceStatus.SETTLING,
    });

    let jogCloseTime = this.getJogCloseTime(bin, initialData);

    this.timers.set(timerKey.jog(scale.id), () => {

      const finishWeight = this.getScaleWeight(scale);
      const d = this.getDeviceStatus(bin.id);
      const s = this.getDeviceStatus(scale.id);

      this.deviceStatus.set(bin.id, {
        ...d,
        weight: this.getBinWeightForStatus(d, finishWeight),
      });

      this.deviceStatus.set(scale.id, {
        ...s,
        weight: finishWeight,
      });

      if (!this.isDemo && bin.autoJog && jogOpenTime) {

        this.timers.set(timerKey.jogUpdate(scale.id), () => {
          const { jogStore, weatherStore } = this.rootStore;
          const scaleWeight = this.getScaleWeight(scale);
          const weight = scaleWeight - startJogWeight;

          if (this.checkIndicatorValueError(scaleWeight) === DeviceStatus.INDICATOR_ERROR) {
            logger.log('indicator error', scale.id, scale.description);
            return;
          }

          logger.log('jog history', material.code, bin.description, jogOpenTime, weight);

          jogStore.saveJogHistory(bin.id, {
            materialDetailId: materialDetails.id,
            deviceId: bin.id,
            openPeriod: jogOpenTime,
            weight,
            moisture: materialDetails.moistureAmount || 0,
            closeDate: moment().tz(this.timezone).format(),
            temperature: weatherStore.temperature || 0,
            humidity: weatherStore.humidity || 0,
            pressure: weatherStore.pressure || 0,
          });

          jogStore.updateJogTable(bin, jogOpenTime, weight);
        }, (materialDetails.scaleSettlingTime || 2) * 1000);

      }

      addFeedLog({
        batchTicketId: initialData.id,
        operationName: FeedOperation.JOG_CLOSE,
        duration: jogCloseTime,
        recommendation: this.rootStore.jogStore.getWeightForTime(
          jogOpenTime,
          bin.autoTuning && bin.jogTableAuto
            ? bin.jogTableAuto
            : bin.jogTable,
        ),
        scaleWeight: this.getScaleWeight(scale),
        ...this.rootStore.weatherStore,
      }, data);

      this.jogMode({ ...data, jogSecondary: !data.jogSecondary, jogCounter: data.jogCounter + 1 }, initialData, mft);

    }, jogCloseTime * 1000);
  },

  runJogTest(this: IBatchStore, material: IMaterial, jogArray: any[], device: any, id: number) {

    if (this.running.length) {
      this.showWarning('Can\'t start jog test while batching!');
      return;
    }

    const materialDetail = this.materialStore.materialDetails[material.materialDetailId];

    if (!materialDetail) {
      this.showWarning('No material set to run Jog Test!');
      return;
    }

    let scale, openPort, scalePorts: any = {};
    const materialCode = this.materialStore.getMaterialName(material);

    if (materialDetail.scaleId) {
      scale = this.devicesStore.getDeviceById(materialDetail.scaleId);
      if (!scale) {
        this.showWarning(`Decum scale #${materialDetail.scaleId} is assigned to material ${materialCode}, but no scale has been found!`);
        return;
      }
      scalePorts = this.getScalePorts(scale, material);
      openPort = scalePorts.openPort;
    } else {
      if (!device) {
        this.showWarning({ key: 'Can\'t start jog test if no bin is assigned to the material!', props: { materialCode }});
        return;
      }
      scale = this.devicesStore.getDeviceById(device.scaleId);
      openPort = device.gateOnePNo;
    }

    const { jogStore } = this.rootStore;

    if (this.isScaleOpen(scale, scalePorts.statusPort)) {
      this.showWarning({ key: 'Can\'t start jog test when scale is open!', props: { scaleName: scale.description }});
      return;
    }

    if (id) {
      jogArray = jogArray.filter(v => v.id === id);
    }

    this.jogTestRunning = true;
    logger.log('jog test start', device.description, material.code, jogArray, id);

    const start = async () => {
      await asyncForEach(jogArray, async (v: any, i: number) => {
        if (!this.jogTestRunning) {
          return;
        }

        if (!floatRemainder(v.open, jogStore.jogInterval) || v.id === 1 || v.id === id) {
          const weightStart = this.getScaleWeight(scale);
          if (scale.decumulative && weightStart <= 0) {
            this.showWarning({ key: 'Can\'t start jog test when scale has no material!', props: { scaleName: scale.description }});
            return;
          }

          if (this.checkIndicatorValueError(weightStart) === DeviceStatus.INDICATOR_ERROR) {
            logger.log('indicator error', scale.id, scale.description);
            return;
          }

          await this.setOutput(openPort, v.open * 1000);
          await waitFor(v.open * 1000);
          await waitFor(materialDetail.scaleSettlingTime * 1000);

          const weightFinish = this.getScaleWeight(scale);

          const weight = scale.decumulative ? weightStart - weightFinish : weightFinish - weightStart;

          logger.log('jog test', i, { ...v, weight });

          // @ts-ignore
          jogStore.jogTable[`weight${v.id}`] = toNumber(convertUnit(weight, this.getUnitById(scale.unitId), materialDetail.itemBatchUnitSymbol), 2);

          const quantity = toNumber(convertUnit(weight, this.getUnitById(scale.unitId), material.itemInventoryUnitSymbol), 4);
          logger.log('update material', material.code, quantity, material.itemInventoryUnitSymbol);

          if (this.canReduceInventory && quantity > 0) {
            this.materialStore.updateMaterialInventoryUse(material, {
              quantity,
              comment: `Jog test open time ${v.open}`,
              binIds: [ device.id ],
            })
              .catch((e: Error) => {
                this.showWarning({ key: 'Can\'t update material inventory!', props: { error: e.message }});
                this.jogTestRunning = false;
              });
          }
        }

      });

      this.jogTestRunning = false;
      logger.log('jog test finish');
    };

    return start();
  },

  stopJogTest(this: IBatchStore) {

    this.jogTestRunning = false;
  },

};
